import { useState, useEffect } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Users,
  AlertTriangle,
} from "lucide-react";
import axios from "axios";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  subMonths,
  addMonths,
} from "date-fns";
import { useTranslation } from "react-i18next";
import { useFormatDate } from "../../utils/dateFormat";
import OwnerBookingRow from "./OwnerBookingRow";

/**
 * Owner-side booking calendar.
 *
 * Layout (per user request, May 2026):
 *   ┌──────────────────────────────────────┐
 *   │           Full-width calendar        │
 *   ├──────────────────────────────────────┤
 *   │       Selected-day bookings list     │   ← uses OwnerBookingRow
 *   │       (full controls — confirm,      │      so admins can confirm/
 *   │        reject, propose-time, cancel, │      reject/etc. in-place
 *   │        notes, mark complete)         │      without leaving the
 *   └──────────────────────────────────────┘      calendar.
 *
 * Calendar cells show ONLY status counts (confirmed / pending) + override
 * indicator. The legacy "X guests" row was removed — the user wanted that
 * info confined to the bookings panel where it belongs next to the actual
 * customer rows.
 */
export default function OwnerCalendar() {
  const { t } = useTranslation();
  const fmt = useFormatDate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayBookings, setDayBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [cancelReasons, setCancelReasons] = useState([]);

  const monthStr = format(currentMonth, "yyyy-MM");

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/owner/calendar?month=${monthStr}`);
      setCalendarData(response.data);
    } catch (error) {
      console.error("Error fetching calendar:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCalendar(); }, [monthStr]); // eslint-disable-line

  // Prefetch cancellation reasons once so each row doesn't re-request.
  useEffect(() => {
    axios.get(`${API}/cancellation-reasons`).then((r) => setCancelReasons(r.data)).catch(() => {});
  }, []);

  const fetchDayBookings = async (dateStr) => {
    setLoadingBookings(true);
    try {
      const response = await axios.get(`${API}/owner/bookings?date_from=${dateStr}&date_to=${dateStr}`);
      setDayBookings(response.data.bookings || []);
    } catch (error) {
      console.error("Error fetching day bookings:", error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    fetchDayBookings(day.date);
  };

  // After any booking mutation in a row, re-pull both lists so the calendar
  // counts AND the under-day list reflect the new state.
  const handleRowChanged = () => {
    fetchCalendar();
    if (selectedDay) fetchDayBookings(selectedDay.date);
  };

  const daysMap = {};
  if (calendarData?.days) {
    calendarData.days.forEach((d) => { daysMap[d.date] = d; });
  }

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const allDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDow = getDay(monthStart); // 0=Sunday
  const paddingDays = startDow === 0 ? 6 : startDow - 1;

  return (
    <div data-testid="owner-calendar-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground">{t("owner_calendar:title")}</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} data-testid="calendar-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[140px] text-center" data-testid="calendar-month-label">
            {fmt(currentMonth, "MMMM yyyy")}
          </span>
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} data-testid="calendar-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse bg-muted h-96 rounded-xl" />
      ) : (
        <>
          {/* Full-width calendar grid */}
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="grid grid-cols-7 border-b border-border">
              {[1, 2, 3, 4, 5, 6, 7].map((offset) => {
                const d = new Date(2024, 0, offset);
                return (
                  <div key={offset} className="py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {fmt(d, "EEE")}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: paddingDays }).map((_, i) => (
                <div key={`pad-${i}`} className="h-24 border-b border-r border-border bg-muted/20" />
              ))}
              {allDays.map((date) => {
                const dateStr = format(date, "yyyy-MM-dd");
                const dayData = daysMap[dateStr];
                const isToday = dateStr === format(new Date(), "yyyy-MM-dd");
                const isSelected = selectedDay?.date === dateStr;
                const isClosed = dayData?.is_closed;

                return (
                  <div
                    key={dateStr}
                    onClick={() => dayData && handleDayClick(dayData)}
                    className={`h-24 border-b border-r border-border p-1.5 cursor-pointer transition-colors relative
                      ${isToday ? "bg-primary/5" : ""}
                      ${isSelected ? "ring-2 ring-primary ring-inset" : ""}
                      ${isClosed ? "bg-red-50/50" : "hover:bg-muted/40"}
                    `}
                    data-testid={`calendar-day-${dateStr}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${isToday ? "bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center" : "text-foreground"}`}>
                        {format(date, "d")}
                      </span>
                      {dayData?.has_override && (
                        <AlertTriangle className="h-3 w-3 text-amber-500" />
                      )}
                    </div>

                    {isClosed ? (
                      <span className="text-[10px] text-red-500 font-medium">
                        {t("owner_calendar:closed_label", { defaultValue: "CLOSED" })}
                      </span>
                    ) : dayData?.total_bookings > 0 ? (
                      // Status counts only — guests count moved to the
                      // under-calendar bookings panel per user request.
                      <div className="space-y-0.5">
                        {dayData.confirmed > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            <span className="text-[10px] text-muted-foreground">
                              {t("owner_calendar:confirmed_count", { count: dayData.confirmed })}
                            </span>
                          </div>
                        )}
                        {dayData.pending > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            <span className="text-[10px] text-muted-foreground">
                              {dayData.pending} {t("booking_status:pending")}
                            </span>
                          </div>
                        )}
                        {dayData.cancelled > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                            <span className="text-[10px] text-muted-foreground">
                              {dayData.cancelled} {t("booking_status:cancelled_by_customer", { defaultValue: "cancelled" }).split(" ")[0].toLowerCase()}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 mt-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              {t("owner_calendar:legend_confirmed")}
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              {t("owner_calendar:legend_pending")}
            </div>
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              {t("owner_calendar:legend_override")}
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded bg-red-200" />
              {t("owner_calendar:legend_closed")}
            </div>
          </div>

          {/* Under-calendar bookings panel — full row controls so the owner
              can confirm / reject / propose-time / cancel / take notes
              without leaving the calendar. */}
          <div className="mt-8" data-testid="calendar-day-bookings">
            {selectedDay ? (
              <>
                <div className="flex items-baseline gap-4 mb-4 pb-3 border-b border-border">
                  <h2 className="text-xl font-medium text-foreground">
                    {fmt(new Date(selectedDay.date), "EEEE, MMMM d")}
                  </h2>
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <CalendarDays className="h-4 w-4" />
                    {t("owner_calendar:bookings_count", { count: selectedDay.total_bookings })}
                  </span>
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {t("booking:guests_count", { count: selectedDay.total_guests })}
                  </span>
                </div>
                {selectedDay.is_closed ? (
                  <div className="text-center py-8 bg-red-50/50 rounded-xl border border-red-100">
                    <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {t("owner_calendar:closed_label", { defaultValue: "Closed" })}
                      {selectedDay.override_note ? `: ${selectedDay.override_note}` : ""}
                    </p>
                  </div>
                ) : loadingBookings ? (
                  <div className="space-y-3">
                    {[1, 2].map((i) => (
                      <div key={i} className="animate-pulse bg-muted h-24 rounded-xl" />
                    ))}
                  </div>
                ) : dayBookings.length > 0 ? (
                  <div className="space-y-4">
                    {dayBookings.map((b) => (
                      <OwnerBookingRow
                        key={b.booking_id}
                        booking={b}
                        onChanged={handleRowChanged}
                        cancelReasons={cancelReasons}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t("owner_calendar:no_bookings_day")}
                  </p>
                )}
              </>
            ) : (
              <div className="text-center py-12 bg-card rounded-xl border border-border">
                <CalendarDays className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{t("owner_calendar:select_day")}</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
